import type { IDBPDatabase, IDBPTransaction } from 'idb';
import type { FenixSchema } from './fenix-db.ts';
import { openFenixDatabase, storageFailure, validDatasetControl } from './fenix-db.ts';
import { canonical } from './canonical.ts';
import {
  failure, validConsumption, validId, validLocalDate, validPlannedMeal,
  validRecipe, validRecipeRevision, validRevision, validShoppingItem,
} from '../domain/nutrition.ts';
import type {
  Consumption, NutritionResult, PlannedMeal, RecipeRevision, ShoppingItem,
} from '../domain/nutrition.ts';
import type {
  ArchiveRecipeCommand, CorrectConsumptionCommand, CreateConsumptionCommand, CreateMealCommand,
  CreateRecipeCommand, CreateShoppingCommand, EditMealCommand,
  EditRecipeCommand, EditShoppingCommand, NutritionCommit, NutritionContext,
  NutritionRepository, PlannedMealView, RecipeView, ResolveMealCommand,
  ShoppingItemView,
} from '../ports/nutrition-repository.ts';

export type NutritionFault =
  | 'after-current' | 'after-history' | 'after-receipt'
  | 'after-control' | 'lost-ack' | 'reread-abort';

export class NutritionLabFaults {
  next: NutritionFault | null = null;
  failNextRead = false;
  take() { const next = this.next; this.next = null; return next; }
}

type Action =
  | 'create-recipe' | 'edit-recipe' | 'archive-recipe'
  | 'create-meal' | 'edit-meal' | 'resolve-meal' | 'improvised' | 'correct-consumption'
  | 'create-shopping' | 'edit-shopping';
interface Receipt {
  operationId: string; datasetId: string; action: Action;
  fingerprint: string; ownerId: string; revision: number;
}
const stores = [
  'control', 'nutrition_recipes', 'nutrition_recipe_revisions',
  'nutrition_planned_meals', 'nutrition_planned_meal_history',
  'nutrition_consumptions', 'nutrition_consumption_history',
  'nutrition_shopping_items', 'nutrition_shopping_item_history', 'nutrition_receipts',
] as const;
type Tx<M extends 'readonly' | 'readwrite'> = IDBPTransaction<FenixSchema, typeof stores, M>;
type WriteTx = Tx<'readwrite'>;
type EntityStore =
  | 'nutrition_recipes' | 'nutrition_recipe_revisions'
  | 'nutrition_planned_meals' | 'nutrition_consumptions'
  | 'nutrition_shopping_items';

const datasetRange = (datasetId: string) =>
  IDBKeyRange.bound([datasetId], [datasetId, []]);
function inject(fault: NutritionFault | null, point: NutritionFault) {
  if (fault === point) throw new DOMException('Synthetic nutrition abort', 'AbortError');
}
function validReceipt(value: unknown): value is Receipt {
  const receipt = value as Receipt;
  return !!receipt && validId(receipt.operationId) && validId(receipt.datasetId)
    && validId(receipt.ownerId) && validRevision(receipt.revision)
    && typeof receipt.fingerprint === 'string'
    && ['create-recipe', 'edit-recipe', 'archive-recipe', 'create-meal',
      'edit-meal', 'resolve-meal', 'improvised', 'correct-consumption', 'create-shopping',
      'edit-shopping'].includes(receipt.action);
}

export class IndexedNutrition implements NutritionRepository {
  private available = true;
  constructor(
    private db: IDBPDatabase<FenixSchema>,
    private faults = new NutritionLabFaults(),
  ) {
    db.addEventListener('versionchange', () => this.close());
    db.addEventListener('close', () => { this.available = false; });
  }
  close() { this.available = false; this.db.close(); }

  private async transaction<M extends 'readonly' | 'readwrite', T>(
    mode: M,
    run: (tx: Tx<M>) => Promise<NutritionResult<T>>,
  ): Promise<NutritionResult<T>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    if (mode === 'readonly' && this.faults.failNextRead) {
      this.faults.failNextRead = false;
      return failure('TRANSACTION_ABORTED');
    }
    let tx: Tx<M> | undefined;
    try {
      tx = this.db.transaction(stores, mode, { durability: 'strict' });
      void tx.done.catch(() => {});
      const result = await run(tx);
      if (!result.ok) {
        tx.abort();
        await tx.done.catch(() => {});
        return result;
      }
      await tx.done;
      return result;
    } catch (error) {
      if (tx) {
        try { tx.abort(); } catch { /* already aborted */ }
        await tx.done.catch(() => {});
      }
      return storageFailure(error);
    }
  }

  private async control<M extends 'readonly' | 'readwrite'>(
    tx: Tx<M>, context?: NutritionContext,
  ) {
    const control = await tx.objectStore('control').get('control');
    if (!validDatasetControl(control)) return failure('CORRUPTION_DETECTED');
    if (control.restoreFence) return failure('RESTORE_IN_PROGRESS');
    if (control.recoveryDatasetId) return failure('CORRUPTION_DETECTED');
    if (context && (context.datasetId !== control.activeDatasetId
      || context.controlEpoch !== control.controlEpoch)) return failure('DATASET_STALE');
    return { ok: true as const, value: control };
  }

  async readContext(): Promise<NutritionResult<NutritionContext>> {
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      return checked.ok ? { ok: true, value: {
        datasetId: checked.value.activeDatasetId,
        controlEpoch: checked.value.controlEpoch,
      } } : checked;
    });
  }

  private async rows<M extends 'readonly' | 'readwrite', T>(
    tx: Tx<M>, name: EntityStore, datasetId: string,
    valid: (value: unknown) => value is T, id: (value: T) => string,
  ): Promise<NutritionResult<readonly T[]>> {
    const output: T[] = [];
    let cursor = await tx.objectStore(name).openCursor(datasetRange(datasetId));
    while (cursor) {
      const row = cursor.value;
      if (!row || row.datasetId !== datasetId || !valid(row.record)
        || cursor.key[1] !== id(row.record)) return failure('CORRUPTION_DETECTED');
      output.push(row.record);
      cursor = await cursor.continue();
    }
    return { ok: true, value: output };
  }

  private queryAll<T>(
    name: EntityStore, valid: (value: unknown) => value is T,
    id: (value: T) => string,
  ): Promise<NutritionResult<readonly T[]>> {
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      return this.rows(tx, name, checked.value.activeDatasetId, valid, id);
    });
  }

  async listRecipes(): Promise<NutritionResult<readonly RecipeView[]>> {
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const datasetId = checked.value.activeDatasetId;
      const recipes = await this.rows(tx, 'nutrition_recipes', datasetId, validRecipe, x => x.recipeId);
      if (!recipes.ok) return recipes;
      const revisions = await this.rows(
        tx, 'nutrition_recipe_revisions', datasetId,
        validRecipeRevision, x => x.recipeRevisionId,
      );
      if (!revisions.ok) return revisions;
      const context = { datasetId, controlEpoch: checked.value.controlEpoch };
      const value: RecipeView[] = [];
      const byId = new Map(revisions.value.map(revision => [revision.recipeRevisionId, revision]));
      for (const recipe of recipes.value) {
        const revision = byId.get(recipe.currentRevisionRef);
        if (!revision || revision.recipeId !== recipe.recipeId) return failure('CORRUPTION_DETECTED');
        value.push({ recipe, revision, context });
      }
      return { ok: true, value };
    });
  }
  async readRecipe(id: string): Promise<NutritionResult<RecipeView>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    const all = await this.listRecipes();
    if (!all.ok) return all;
    const found = all.value.find(x => x.recipe.recipeId === id);
    return found ? { ok: true, value: found } : failure('NOT_FOUND');
  }
  async readRecipeRevision(id: string): Promise<NutritionResult<RecipeRevision>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    const all = await this.queryAll(
      'nutrition_recipe_revisions', validRecipeRevision, x => x.recipeRevisionId,
    );
    if (!all.ok) return all;
    const found = all.value.find(x => x.recipeRevisionId === id);
    return found ? { ok: true, value: found } : failure('NOT_FOUND');
  }

  async listPlannedMeals(from: string, to: string): Promise<NutritionResult<readonly PlannedMealView[]>> {
    if (!validLocalDate(from) || !validLocalDate(to) || from > to) return failure('VALIDATION_ERROR');
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const context = {
        datasetId: checked.value.activeDatasetId,
        controlEpoch: checked.value.controlEpoch,
      };
      const all = await this.rows(
        tx, 'nutrition_planned_meals', context.datasetId,
        validPlannedMeal, x => x.plannedMealId,
      );
      if (!all.ok) return all;
      return { ok: true, value: all.value
        .filter(meal => meal.localDate >= from && meal.localDate <= to)
        .map(meal => ({ meal, context })) };
    });
  }
  async readPlannedMeal(id: string): Promise<NutritionResult<PlannedMealView>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const row = await tx.objectStore('nutrition_planned_meals').get([
        checked.value.activeDatasetId, id,
      ]);
      if (!row) return failure('NOT_FOUND');
      if (row.datasetId !== checked.value.activeDatasetId
        || !validPlannedMeal(row.record)
        || row.record.plannedMealId !== id) return failure('CORRUPTION_DETECTED');
      return { ok: true, value: { meal: row.record, context: {
        datasetId: checked.value.activeDatasetId,
        controlEpoch: checked.value.controlEpoch,
      } } };
    });
  }
  async readPlannedMealRevision(id: string, revision: number): Promise<NutritionResult<PlannedMeal>> {
    if (!validId(id) || !validRevision(revision)) return failure('VALIDATION_ERROR');
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const row = await tx.objectStore('nutrition_planned_meal_history').get([
        checked.value.activeDatasetId, id, revision,
      ]);
      if (!row) return failure('NOT_FOUND');
      if (row.datasetId !== checked.value.activeDatasetId || !validPlannedMeal(row.record)
        || row.record.plannedMealId !== id || row.record.revision !== revision) {
        return failure('CORRUPTION_DETECTED');
      }
      return { ok: true, value: row.record };
    });
  }

  async readMealResolution(id: string): Promise<NutritionResult<Consumption | null>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    const all = await this.queryAll('nutrition_consumptions', validConsumption, x => x.consumptionId);
    if (!all.ok) return all;
    const found = all.value.filter(x => x.plannedMealId === id);
    return found.length > 1 ? failure('CORRUPTION_DETECTED') : { ok: true, value: found[0] ?? null };
  }
  async readMealResolutions(ids: readonly string[]): Promise<NutritionResult<readonly Consumption[]>> {
    if (!Array.isArray(ids) || !ids.every(validId)) return failure('VALIDATION_ERROR');
    const selected = new Set(ids);
    const all = await this.queryAll('nutrition_consumptions', validConsumption, x => x.consumptionId);
    if (!all.ok) return all;
    const found = all.value.filter(x => x.plannedMealId && selected.has(x.plannedMealId));
    if (new Set(found.map(x => x.plannedMealId)).size !== found.length) return failure('CORRUPTION_DETECTED');
    return { ok: true, value: found };
  }
  async listConsumptions(from: string, to: string): Promise<NutritionResult<readonly Consumption[]>> {
    if (!validLocalDate(from) || !validLocalDate(to) || from > to) return failure('VALIDATION_ERROR');
    const all = await this.queryAll('nutrition_consumptions', validConsumption, x => x.consumptionId);
    return all.ok ? { ok: true, value: all.value.filter(x => x.localDate >= from && x.localDate <= to) } : all;
  }
  async readConsumptionRevision(id: string, revision: number): Promise<NutritionResult<Consumption>> {
    if (!validId(id) || !validRevision(revision)) return failure('VALIDATION_ERROR');
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const row = await tx.objectStore('nutrition_consumption_history').get([
        checked.value.activeDatasetId, id, revision,
      ]);
      if (!row) return failure('NOT_FOUND');
      if (row.datasetId !== checked.value.activeDatasetId || !validConsumption(row.record)
        || row.record.consumptionId !== id || row.record.revision !== revision) {
        return failure('CORRUPTION_DETECTED');
      }
      return { ok: true, value: row.record };
    });
  }
  listShoppingItems(): Promise<NutritionResult<readonly ShoppingItemView[]>> {
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const context = {
        datasetId: checked.value.activeDatasetId,
        controlEpoch: checked.value.controlEpoch,
      };
      const all = await this.rows(
        tx, 'nutrition_shopping_items', context.datasetId,
        validShoppingItem, x => x.shoppingItemId,
      );
      return all.ok
        ? { ok: true, value: all.value.map(item => ({ item, context })) }
        : all;
    });
  }
  async readShoppingItemRevision(id: string, revision: number): Promise<NutritionResult<ShoppingItem>> {
    if (!validId(id) || !validRevision(revision)) return failure('VALIDATION_ERROR');
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx);
      if (!checked.ok) return checked;
      const datasetId = checked.value.activeDatasetId;
      const row = await tx.objectStore('nutrition_shopping_item_history').get([
        datasetId, id, revision,
      ]);
      if (row) {
        if (row.datasetId !== datasetId || !validShoppingItem(row.record)
          || row.record.shoppingItemId !== id || row.record.revision !== revision) {
          return failure('CORRUPTION_DETECTED');
        }
        return { ok: true, value: row.record };
      }
      const current = await tx.objectStore('nutrition_shopping_items').get([datasetId, id]);
      if (!current) return failure('NOT_FOUND');
      if (current.datasetId !== datasetId || !validShoppingItem(current.record)
        || current.record.shoppingItemId !== id) return failure('CORRUPTION_DETECTED');
      return current.record.revision === revision
        ? { ok: true, value: current.record }
        : failure('NOT_FOUND');
    });
  }

  private async write<C extends { operationId: string; context: NutritionContext }>(
    action: Action,
    input: C,
    ownerId: string,
    revision: number,
    apply: (
      tx: WriteTx, datasetId: string,
      checkpoint: (point: 'after-current' | 'after-history') => void,
      snapshot: C,
    ) => Promise<NutritionResult<void>>,
  ): Promise<NutritionResult<NutritionCommit>> {
    if (!input || !validId(input.operationId) || !input.context
      || !validId(input.context.datasetId)
      || !Number.isSafeInteger(input.context.controlEpoch) || input.context.controlEpoch < 0
      || !validId(ownerId) || !validRevision(revision)) return failure('VALIDATION_ERROR');
    let command: typeof input;
    let fingerprint: string;
    try {
      command = structuredClone(input);
      fingerprint = canonical({ kind: 'nutrition', action, command });
    } catch { return failure('VALIDATION_ERROR'); }
    let lostAcknowledgment = false;
    let failReread = false;
    const result = await this.transaction<'readwrite', NutritionCommit>('readwrite', async tx => {
      const checked = await this.control(tx, command.context);
      if (!checked.ok) return checked;
      const control = checked.value;
      const datasetId = control.activeDatasetId;
      const stored = await tx.objectStore('nutrition_receipts').get([datasetId, command.operationId]);
      if (stored) {
        if (stored.datasetId !== datasetId || !validReceipt(stored.record)
          || stored.record.datasetId !== datasetId || stored.record.operationId !== command.operationId) return failure('CORRUPTION_DETECTED');
        if (stored.record.fingerprint !== fingerprint) return failure('INVARIANT_CONFLICT');
        if (stored.record.action !== action || stored.record.ownerId !== ownerId
          || stored.record.revision !== revision) return failure('CORRUPTION_DETECTED');
        return { ok: true, value: { status: 'REPLAYED' as const,
          receipt: { operationId: command.operationId, ownerId, revision } } };
      }
      if (!Number.isSafeInteger(control.datasetRevision + 1)) return failure('INVARIANT_CONFLICT');
      const fault = this.faults.take();
      const applied = await apply(tx, datasetId, point => inject(fault, point), command);
      if (!applied.ok) return applied;
      const receipt: Receipt = { operationId: command.operationId, datasetId,
        action, fingerprint, ownerId, revision };
      await tx.objectStore('nutrition_receipts').add(
        { datasetId, record: receipt }, [datasetId, command.operationId],
      );
      inject(fault, 'after-receipt');
      await tx.objectStore('control').put({ ...control, datasetRevision: control.datasetRevision + 1 });
      inject(fault, 'after-control');
      lostAcknowledgment = fault === 'lost-ack';
      failReread = fault === 'reread-abort';
      return { ok: true, value: { status: 'APPLIED' as const,
        receipt: { operationId: command.operationId, ownerId, revision } } };
    });
    if (result.ok && failReread) this.faults.failNextRead = true;
    return result.ok && lostAcknowledgment ? failure('STORAGE_UNAVAILABLE') : result;
  }

  private async recipeReference(tx: WriteTx, datasetId: string, ref: string): Promise<NutritionResult<void>> {
    const row = await tx.objectStore('nutrition_recipe_revisions').get([datasetId, ref]);
    if (!row) return failure('NOT_FOUND');
    if (row.datasetId !== datasetId || !validRecipeRevision(row.record) || row.record.recipeRevisionId !== ref) return failure('CORRUPTION_DETECTED');
    return { ok: true, value: undefined };
  }

  createRecipe(command: CreateRecipeCommand) {
    if (!command || !validRecipe(command.recipe) || !validRecipeRevision(command.revision)
      || command.recipe.recipeId !== command.revision.recipeId
      || command.recipe.currentRevisionRef !== command.revision.recipeRevisionId
      || command.revision.revision !== 1) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.write('create-recipe', command, command.recipe.recipeId, 1,
      async (tx, datasetId, checkpoint, command) => {
        if (await tx.objectStore('nutrition_recipes').get([datasetId, command.recipe.recipeId])
          || await tx.objectStore('nutrition_recipe_revisions').get([datasetId, command.revision.recipeRevisionId])) {
          return failure('INVARIANT_CONFLICT');
        }
        await tx.objectStore('nutrition_recipes').add(
          { datasetId, record: command.recipe }, [datasetId, command.recipe.recipeId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_recipe_revisions').add(
          { datasetId, record: command.revision }, [datasetId, command.revision.recipeRevisionId],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  editRecipe(command: EditRecipeCommand) {
    if (!command || !validId(command.recipeId) || !validRevision(command.expectedRevision)
      || !validRecipeRevision(command.revision) || command.revision.recipeId !== command.recipeId
      || command.revision.revision !== command.expectedRevision + 1) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('edit-recipe', command, command.recipeId, command.revision.revision,
      async (tx, datasetId, checkpoint, command) => {
        const row = await tx.objectStore('nutrition_recipes').get([datasetId, command.recipeId]);
        if (!row) return failure('NOT_FOUND');
        if (row.datasetId !== datasetId || !validRecipe(row.record) || row.record.recipeId !== command.recipeId) return failure('CORRUPTION_DETECTED');
        if (row.record.archived) return failure('INVARIANT_CONFLICT');
        const previous = await tx.objectStore('nutrition_recipe_revisions').get([
          datasetId, row.record.currentRevisionRef,
        ]);
        if (!previous || previous.datasetId !== datasetId || !validRecipeRevision(previous.record) || previous.record.recipeId !== command.recipeId || previous.record.recipeRevisionId !== row.record.currentRevisionRef) return failure('CORRUPTION_DETECTED');
        if (previous.record.revision !== command.expectedRevision) return failure('REVISION_CONFLICT');
        if (await tx.objectStore('nutrition_recipe_revisions').get([
          datasetId, command.revision.recipeRevisionId,
        ])) return failure('INVARIANT_CONFLICT');
        await tx.objectStore('nutrition_recipe_revisions').add(
          { datasetId, record: command.revision }, [datasetId, command.revision.recipeRevisionId],
        );
        checkpoint('after-history');
        await tx.objectStore('nutrition_recipes').put(
          { datasetId, record: { ...row.record, currentRevisionRef: command.revision.recipeRevisionId } },
          [datasetId, command.recipeId],
        );
        checkpoint('after-current');
        return { ok: true, value: undefined };
      });
  }

  archiveRecipe(command: ArchiveRecipeCommand) {
    if (!command || !validId(command.recipeId) || !validRevision(command.expectedRevision)) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('archive-recipe', command, command.recipeId, command.expectedRevision,
      async (tx, datasetId, checkpoint, command) => {
        const row = await tx.objectStore('nutrition_recipes').get([datasetId, command.recipeId]);
        if (!row) return failure('NOT_FOUND');
        if (row.datasetId !== datasetId || !validRecipe(row.record) || row.record.recipeId !== command.recipeId) return failure('CORRUPTION_DETECTED');
        const revision = await tx.objectStore('nutrition_recipe_revisions').get([
          datasetId, row.record.currentRevisionRef,
        ]);
        if (!revision || revision.datasetId !== datasetId || !validRecipeRevision(revision.record) || revision.record.recipeId !== command.recipeId || revision.record.recipeRevisionId !== row.record.currentRevisionRef) return failure('CORRUPTION_DETECTED');
        if (row.record.archived) return failure('INVARIANT_CONFLICT');
        if (revision.record.revision !== command.expectedRevision) return failure('REVISION_CONFLICT');
        await tx.objectStore('nutrition_recipes').put(
          { datasetId, record: { ...row.record, archived: true } }, [datasetId, command.recipeId],
        );
        checkpoint('after-current');
        return { ok: true, value: undefined };
      });
  }

  createPlannedMeal(command: CreateMealCommand) {
    if (!command || !validPlannedMeal(command.meal) || command.meal.revision !== 1) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('create-meal', command, command.meal.plannedMealId, 1,
      async (tx, datasetId, checkpoint, command) => {
        if (await tx.objectStore('nutrition_planned_meals').get([
          datasetId, command.meal.plannedMealId,
        ])) return failure('INVARIANT_CONFLICT');
        if (command.meal.recipeRevisionRef) {
          const reference = await this.recipeReference(tx, datasetId, command.meal.recipeRevisionRef);
          if (!reference.ok) return reference;
        }
        await tx.objectStore('nutrition_planned_meals').add(
          { datasetId, record: command.meal }, [datasetId, command.meal.plannedMealId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_planned_meal_history').add(
          { datasetId, record: command.meal }, [datasetId, command.meal.plannedMealId, 1],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  editPlannedMeal(command: EditMealCommand) {
    if (!command || !validId(command.plannedMealId) || !validRevision(command.expectedRevision)
      || !validPlannedMeal(command.meal) || command.meal.plannedMealId !== command.plannedMealId
      || command.meal.revision !== command.expectedRevision + 1) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('edit-meal', command, command.plannedMealId, command.meal.revision,
      async (tx, datasetId, checkpoint, command) => {
        const row = await tx.objectStore('nutrition_planned_meals').get([
          datasetId, command.plannedMealId,
        ]);
        if (!row) return failure('NOT_FOUND');
        if (row.datasetId !== datasetId || !validPlannedMeal(row.record) || row.record.plannedMealId !== command.plannedMealId) return failure('CORRUPTION_DETECTED');
        if (row.record.revision !== command.expectedRevision) return failure('REVISION_CONFLICT');
        if (command.meal.recipeRevisionRef) {
          const reference = await this.recipeReference(tx, datasetId, command.meal.recipeRevisionRef);
          if (!reference.ok) return reference;
        }
        await tx.objectStore('nutrition_planned_meals').put(
          { datasetId, record: command.meal }, [datasetId, command.plannedMealId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_planned_meal_history').add(
          { datasetId, record: command.meal },
          [datasetId, command.plannedMealId, command.meal.revision],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  resolveMeal(command: ResolveMealCommand) {
    const consumption = command?.consumption;
    if (!validConsumption(consumption) || consumption.revision !== 1 || !consumption.plannedMealId
      || consumption.plannedMealRevision !== command.expectedMealRevision) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('resolve-meal', command, consumption.consumptionId, 1,
      async (tx, datasetId, checkpoint, command) => {
        const consumption = command.consumption;
        const meal = await tx.objectStore('nutrition_planned_meals').get([
          datasetId, consumption.plannedMealId!,
        ]);
        if (!meal) return failure('NOT_FOUND');
        if (meal.datasetId !== datasetId || !validPlannedMeal(meal.record) || meal.record.plannedMealId !== consumption.plannedMealId) return failure('CORRUPTION_DETECTED');
        if (meal.record.revision !== command.expectedMealRevision) return failure('REVISION_CONFLICT');
        if (await tx.objectStore('nutrition_consumptions').get([datasetId, consumption.consumptionId])) return failure('INVARIANT_CONFLICT');
        let cursor = await tx.objectStore('nutrition_consumptions').openCursor(datasetRange(datasetId));
        while (cursor) {
          if (cursor.value.datasetId !== datasetId || !validConsumption(cursor.value.record) || cursor.key[1] !== cursor.value.record.consumptionId) return failure('CORRUPTION_DETECTED');
          if (cursor.value.record.plannedMealId === consumption.plannedMealId) {
            return failure('INVARIANT_CONFLICT');
          }
          cursor = await cursor.continue();
        }
        await tx.objectStore('nutrition_consumptions').add(
          { datasetId, record: consumption }, [datasetId, consumption.consumptionId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_consumption_history').add(
          { datasetId, record: consumption }, [datasetId, consumption.consumptionId, 1],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  createImprovised(command: CreateConsumptionCommand) {
    const consumption = command?.consumption;
    if (!validConsumption(consumption) || consumption.revision !== 1 || consumption.plannedMealId !== undefined) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('improvised', command, consumption.consumptionId, 1,
      async (tx, datasetId, checkpoint, command) => {
        const consumption = command.consumption;
        if (await tx.objectStore('nutrition_consumptions').get([
          datasetId, consumption.consumptionId,
        ])) return failure('INVARIANT_CONFLICT');
        await tx.objectStore('nutrition_consumptions').add(
          { datasetId, record: consumption }, [datasetId, consumption.consumptionId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_consumption_history').add(
          { datasetId, record: consumption }, [datasetId, consumption.consumptionId, 1],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  correctConsumption(command: CorrectConsumptionCommand) {
    const consumption = command?.consumption;
    if (!command || !validId(command.consumptionId) || !validRevision(command.expectedRevision)
      || !validConsumption(consumption) || consumption.consumptionId !== command.consumptionId
      || consumption.revision !== command.expectedRevision + 1) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('correct-consumption', command, command.consumptionId, consumption.revision,
      async (tx, datasetId, checkpoint, command) => {
        const row = await tx.objectStore('nutrition_consumptions').get([
          datasetId, command.consumptionId,
        ]);
        if (!row) return failure('NOT_FOUND');
        if (row.datasetId !== datasetId || !validConsumption(row.record)
          || row.record.consumptionId !== command.consumptionId) return failure('CORRUPTION_DETECTED');
        if (row.record.revision !== command.expectedRevision) return failure('REVISION_CONFLICT');
        const next = command.consumption;
        if (next.localDate !== row.record.localDate || next.resolution !== row.record.resolution
          || next.plannedMealId !== row.record.plannedMealId
          || next.plannedMealRevision !== row.record.plannedMealRevision) return failure('INVARIANT_CONFLICT');
        await tx.objectStore('nutrition_consumptions').put(
          { datasetId, record: next }, [datasetId, command.consumptionId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_consumption_history').add(
          { datasetId, record: next }, [datasetId, command.consumptionId, next.revision],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  createShoppingItem(command: CreateShoppingCommand) {
    if (!command || !validShoppingItem(command.item) || command.item.revision !== 1) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('create-shopping', command, command.item.shoppingItemId, 1,
      async (tx, datasetId, checkpoint, command) => {
        if (await tx.objectStore('nutrition_shopping_items').get([
          datasetId, command.item.shoppingItemId,
        ])) return failure('INVARIANT_CONFLICT');
        await tx.objectStore('nutrition_shopping_items').add(
          { datasetId, record: command.item }, [datasetId, command.item.shoppingItemId],
        );
        checkpoint('after-current');
        await tx.objectStore('nutrition_shopping_item_history').add(
          { datasetId, record: command.item }, [datasetId, command.item.shoppingItemId, 1],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }

  editShoppingItem(command: EditShoppingCommand) {
    if (!command || !validId(command.shoppingItemId) || !validRevision(command.expectedRevision)
      || !validShoppingItem(command.item) || command.item.shoppingItemId !== command.shoppingItemId
      || command.item.revision !== command.expectedRevision + 1) {
      return Promise.resolve(failure('VALIDATION_ERROR'));
    }
    return this.write('edit-shopping', command, command.shoppingItemId, command.item.revision,
      async (tx, datasetId, checkpoint, command) => {
        const row = await tx.objectStore('nutrition_shopping_items').get([
          datasetId, command.shoppingItemId,
        ]);
        if (!row) return failure('NOT_FOUND');
        if (row.datasetId !== datasetId || !validShoppingItem(row.record) || row.record.shoppingItemId !== command.shoppingItemId) return failure('CORRUPTION_DETECTED');
        if (row.record.revision !== command.expectedRevision) return failure('REVISION_CONFLICT');
        const history = tx.objectStore('nutrition_shopping_item_history');
        const previous = await history.get([datasetId, command.shoppingItemId, row.record.revision]);
        if (previous && (previous.datasetId !== datasetId || !validShoppingItem(previous.record)
          || previous.record.shoppingItemId !== command.shoppingItemId
          || previous.record.revision !== row.record.revision
          || canonical(previous.record) !== canonical(row.record))) return failure('CORRUPTION_DETECTED');
        if (await history.get([datasetId, command.shoppingItemId, command.item.revision])) {
          return failure('CORRUPTION_DETECTED');
        }
        await tx.objectStore('nutrition_shopping_items').put(
          { datasetId, record: command.item }, [datasetId, command.shoppingItemId],
        );
        checkpoint('after-current');
        if (!previous) await history.add(
          { datasetId, record: row.record }, [datasetId, command.shoppingItemId, row.record.revision],
        );
        await history.add(
          { datasetId, record: command.item }, [datasetId, command.shoppingItemId, command.item.revision],
        );
        checkpoint('after-history');
        return { ok: true, value: undefined };
      });
  }
}

export async function openNutrition(
  name: string,
  faults = new NutritionLabFaults(),
): Promise<NutritionResult<IndexedNutrition>> {
  const opened = await openFenixDatabase(name);
  return opened.ok ? { ok: true, value: new IndexedNutrition(opened.value, faults) } : opened;
}
